import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLocations } from '../locationmasters/store/slice';
import { selectLocationData, selectLocationLoading } from '../locationmasters/store/selectors';
import { InventoryScanningService } from '../../service/api/inventory-scanning-service';
import { Table } from 'antd';
import { Badge, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MapPin, RefreshCcw, Target, Activity, Trash2 } from 'react-feather';
import { toast } from 'react-toastify';

const InventoryScanningView = () => {
  const dispatch = useDispatch();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [audits, setAudits] = useState([]);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [auditsLoading, setAuditsLoading] = useState(false);
  const [scanData, setScanData] = useState('');
  const [scannedItems, setScannedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [isLocationListVisible, setIsLocationListVisible] = useState(false);
  const [fetchedSerials, setFetchedSerials] = useState([]);
  const locationInputRef = useRef(null);
  const scanInputRef = useRef(null);
  const scanTimeoutRef = useRef(null);

  // Fetch locations
  const locations = useSelector(selectLocationData);
  const locationsLoading = useSelector(selectLocationLoading);

  const locationName = React.useMemo(() => {
    if (!selectedLocation) {
      return 'No location selected';
    }
    return selectedLocation.name || selectedLocation.locationName || 'Unnamed location';
  }, [selectedLocation]);

  const auditName = React.useMemo(() => {
    if (!selectedAudit) {
      return 'No audit selected';
    }
    return selectedAudit.auditName || 'Unnamed audit';
  }, [selectedAudit]);

  const formatTimestamp = React.useCallback(value => {
    if (!value) {
      return 'Just now';
    }
    try {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value));
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Just now';
    }
  }, []);

  const summaryDetails = React.useMemo(
    () => ({
      totalCount: scannedItems.length,
    }),
    [scannedItems],
  );

  const scannerHelperText = selectedLocation
    ? `Scans will be stored under ${locationName}.`
    : 'Pick a location to unlock the scanner input.';

  const handleLocationRefresh = () => {
    if (!locationsLoading && !isSubmitting) {
      dispatch(fetchLocations({}));
    }
  };

  const handleFocusScanner = () => {
    if (scanInputRef.current) {
      scanInputRef.current.focus();
    }
  };

  const loadAudits = React.useCallback(() => {
    setAuditsLoading(true);
    InventoryScanningService.getInventoryAudits()
      .then(response => {
        setAuditsLoading(false);
        const auditList = Array.isArray(response?.response) ? response.response : [];
        setAudits(auditList);
        const defaultAudit =
          auditList.find(audit => (audit.status || '').toLowerCase() === 'in progress') ||
          auditList[0] ||
          null;
        setSelectedAudit(defaultAudit);
      })
      .catch(error => {
        setAuditsLoading(false);
        console.error('Error loading audits:', error);
        setAudits([]);
        setSelectedAudit(null);
      });
  }, []);

  const handleAuditChange = event => {
    const auditId = Number(event.target.value);
    const foundAudit = audits.find(audit => audit.id === auditId) || null;
    setSelectedAudit(foundAudit);
    setFetchedSerials([]);
    setScannedItems([]);
  };

  const handleAuditRefresh = () => {
    if (!auditsLoading && !isSubmitting) {
      loadAudits();
    }
  };

  useEffect(() => {
    dispatch(fetchLocations({}));
    loadAudits();
    return () => {
      // Cleanup timeout on unmount
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
    };
  }, [dispatch, loadAudits]);

  useEffect(() => {
    setScannedItems([]);
    if (selectedLocation && scanInputRef.current) {
      scanInputRef.current.focus();
    }
    if (selectedLocation) {
      setLocationQuery(selectedLocation.name || selectedLocation.locationName || '');
    }
  }, [selectedLocation]);

  const findLocationMatch = React.useCallback(
    value => {
      if (!value) {
        return null;
      }
      const normalizedValue = value.trim().toLowerCase();
      return (
        (locations || []).find(location => {
          const name = (location.name || location.locationName || '').toLowerCase();
          const idValue = String(location.id || location.locationId || '').toLowerCase();
          return name === normalizedValue || idValue === normalizedValue;
        }) || null
      );
    },
    [locations],
  );

  const handleLocationSelect = location => {
    setSelectedLocation(location);
    setLocationQuery(location.name || location.locationName || '');
    setIsLocationListVisible(false);
    setFetchedSerials([]);
    setScannedItems([]);
  };

  const handleLocationChange = event => {
    const value = event.target.value;
    setLocationQuery(value);
    setIsLocationListVisible(true);

    if (!value) {
      setSelectedLocation(null);
      setFetchedSerials([]);
      setScannedItems([]);
      return;
    }

    const matchedLocation = findLocationMatch(value);
    if (matchedLocation) {
      handleLocationSelect(matchedLocation);
    }
  };

  const handleLocationKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const matchedLocation = findLocationMatch(locationQuery);
      if (matchedLocation) {
        handleLocationSelect(matchedLocation);
      }
    }
  };

  const filteredLocations = React.useMemo(() => {
    const query = locationQuery.trim().toLowerCase();
    if (!query) {
      return locations || [];
    }
    return (locations || []).filter(location => {
      const name = (location.name || location.locationName || '').toLowerCase();
      const idValue = String(location.id || location.locationId || '').toLowerCase();
      return name.includes(query) || idValue.includes(query);
    });
  }, [locationQuery, locations]);

  const handleScanSubmit = scannedValue => {
    if (isSubmitting) {
      return;
    }

    if (!selectedLocation) {
      alert('Please select a location first');
      setScanData(''); // Clear the field even if location not selected
      return;
    }

    if (!scannedValue || scannedValue.trim() === '') {
      return;
    }

    const serialNumber = scannedValue.trim();
    const locationId = selectedLocation.id || selectedLocation.locationId;
    const userId = localStorage.getItem('UserId') || null;

    if (!locationId) {
      alert('Invalid location selected');
      return;
    }

    // Show item in table immediately (optimistic update) with counts
    setScannedItems(prevItems => {
      const existingIndex = prevItems.findIndex(item => item.serialNumber === serialNumber);
      const now = new Date().toISOString();

      if (existingIndex !== -1) {
        const existingItem = prevItems[existingIndex];
        const updatedItem = {
          ...existingItem,
          count: (existingItem.count || 1) + 1,
          createdAt: now,
        };
        const withoutExisting = prevItems.filter(item => item.serialNumber !== serialNumber);
        return [updatedItem, ...withoutExisting];
      }

      const newItem = {
        key: serialNumber,
        serialNumber: serialNumber,
        createdBy: userId || 'You',
        createdAt: now,
        count: 1,
      };

      setCurrentPage(1);
      return [newItem, ...prevItems];
    });
    setScanData(''); // Clear the input field

    if (scanInputRef.current) {
      scanInputRef.current.focus();
    }
  };

  // Handle barcode scan input - only update state, don't process yet
  const handleScanChange = e => {
    if (isSubmitting) {
      return;
    }
    const value = e.target.value;
    setScanData(value);

    // Clear any existing timeout
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }

    // Set a very short timeout to detect when scanning is complete
    // Barcode scanners typically send all characters very quickly (< 50ms)
    // If no new input comes for 50ms, assume scanning is complete and save immediately
    scanTimeoutRef.current = setTimeout(() => {
      if (value && value.trim() !== '') {
        handleScanSubmit(value);
      }
    }, 50);
  };

  const handleDeleteRow = key => {
    setScannedItems(prevItems => {
      const nextItems = prevItems.filter(item => item.key !== key);
      const nextPageMax = Math.max(1, Math.ceil(nextItems.length / pageSize));
      if (currentPage > nextPageMax) {
        setCurrentPage(nextPageMax);
      }
      return nextItems;
    });
  };

  const handleTableChange = pagination => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
  };

  const handleSubmitScans = () => {
    if (isSubmitting) {
      return;
    }

    if (!selectedAudit) {
      alert('Please select an audit before submitting.');
      return;
    }

    if (!selectedLocation) {
      alert('Please select a location before submitting.');
      return;
    }

    if (!scannedItems.length && fetchedSerials.length === 0) {
      alert('No scanned items to submit.');
      return;
    }

    const userId = localStorage.getItem('UserId') || null;
    const currentSerials = scannedItems.map(item => item.serialNumber);
    const deletedSerialNumbers = fetchedSerials.filter(serial => !currentSerials.includes(serial));

    const payload = {
      auditId: selectedAudit.id,
      locationId: selectedLocation.id || selectedLocation.locationId,
      items: scannedItems.map(item => ({
        serialNumber: item.serialNumber,
        count: item.count || 1,
      })),
      deletedSerialNumbers: deletedSerialNumbers,
      submittedBy: userId,
    };

    setIsSubmitting(true);
    InventoryScanningService.saveInventoryScanningBatch(payload)
      .then(response => {
        if (response?.status === 200) {
          setFetchedSerials(scannedItems.map(item => item.serialNumber));
          setScannedItems([]);
          toast.success('Submitted successfully');
        } else {
          alert(response?.errorMessage?.message || 'Submit failed');
        }
      })
      .catch(error => {
        console.error('Error submitting scans:', error);
        alert('Submit failed. Please try again.');
      })
      .finally(() => {
        setIsSubmitting(false);
        handleFocusScanner();
      });
  };

  const handleFetchDetails = () => {
    if (isSubmitting || isFetchingDetails) {
      return;
    }

    if (!selectedAudit) {
      alert('Please select an audit before fetching.');
      return;
    }

    if (!selectedLocation) {
      alert('Please select a location before fetching.');
      return;
    }

    setIsFetchingDetails(true);
    const payload = {
      auditId: selectedAudit.id,
      locationId: selectedLocation.id || selectedLocation.locationId,
    };

    InventoryScanningService.getInventoryScanningByAuditLocation(payload)
      .then(response => {
        const items = Array.isArray(response?.response) ? response.response : [];
        const mapped = items.map(item => ({
          key: item.serialNumber,
          serialNumber: item.serialNumber,
          createdBy: item.createdBy || 'N/A',
          createdAt:
            item.createdAt || item.updatedAt || item.submittedAt || new Date().toISOString(),
          count: item.count || item.countValue || 1,
        }));
        setScannedItems(mapped);
        setFetchedSerials(mapped.map(item => item.serialNumber));
        setCurrentPage(1);
      })
      .catch(error => {
        console.error('Error fetching scan details:', error);
        alert('Failed to fetch details.');
      })
      .finally(() => {
        setIsFetchingDetails(false);
        handleFocusScanner();
      });
  };

  const handleClearScans = () => {
    if (isSubmitting) {
      return;
    }
    if (scannedItems.length > 0) {
      const confirmed = window.confirm('Unsaved data will be lost. Do you want to continue?');
      if (!confirmed) {
        return;
      }
    }
    setScannedItems([]);
    setCurrentPage(1);
  };

  // Handle Enter key press (barcode scanners usually send Enter at the end)
  // Use onKeyDown for faster response than onKeyPress
  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Clear the timeout since we're processing immediately
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
      // Get current value directly from input to ensure we have the latest
      const currentValue = e.target.value;
      if (currentValue && currentValue.trim() !== '') {
        handleScanSubmit(currentValue);
      }
    }
  };

  // Table columns
  const columns = React.useMemo(
    () => [
      {
        title: 'Sl. No',
        dataIndex: 'rowNumber',
        key: 'rowNumber',
        width: '10%',
        render: (value, record, index) => <span className="text-muted">{index + 1}</span>,
      },
      {
        title: 'Serial Number',
        dataIndex: 'serialNumber',
        key: 'serialNumber',
        width: '32%',
        render: value => <span className="fw-semibold text-dark">{value}</span>,
      },
      {
        title: 'Count',
        dataIndex: 'count',
        key: 'count',
        width: '12%',
        render: value => <span className="text-dark fw-semibold">{value || 1}</span>,
      },
      {
        title: 'Scanned On',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: '36%',
        render: value => formatTimestamp(value),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: '10%',
        render: (value, record) => (
          <Button
            variant="link"
            className="text-danger p-0 d-inline-flex align-items-center"
            onClick={() => handleDeleteRow(record.key)}
            disabled={isSubmitting}
          >
            <Trash2 size={16} />
          </Button>
        ),
      },
    ],
    [formatTimestamp],
  );

  const pagedScannedItems = React.useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return scannedItems.slice(start, end);
  }, [scannedItems, currentPage, pageSize]);

  return (
    <>
      {isSubmitting && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ background: 'rgba(255,255,255,0.6)', zIndex: 2000 }}
        >
          <div className="d-flex justify-content-center align-items-center h-100">
            <div className="spinner-border text-primary" role="status" />
          </div>
        </div>
      )}
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header transfer">
            <div className="add-item d-flex flex-wrap gap-3 w-100 justify-content-between">
              <div className="page-title">
                <h4 className="mb-1">Inventory Cycle Count</h4>
                <p className="text-muted mb-0">
                  Select a location, keep the scanner focused, and watch incoming scans in real
                  time.
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Badge bg="light" className="text-dark px-3 py-2">
                  <Activity size={14} className="me-2 text-info" />
                  {auditName}
                </Badge>
                <Badge bg="light" className="text-dark px-3 py-2">
                  <MapPin size={14} className="me-2 text-primary" />
                  {locationName}
                </Badge>
                <Badge bg="light" className="text-dark px-3 py-2">
                  <Activity size={14} className="me-2 text-success" />
                  {summaryDetails.totalCount} scanned
                </Badge>
              </div>
            </div>
          </div>

          <div className="card table-list-card">
            <div className="card-body">
              <div className="row g-4 align-items-start">
                <div className="col-xl-4">
                  <div className="border rounded p-3 h-100 bg-light">
                    <div className="d-flex align-items-start justify-content-between mb-3">
                      <div>
                        <h6 className="mb-1">Step 1 · Choose an audit</h6>
                        <p className="text-muted small mb-0">
                          Default picks the first audit that is In Progress.
                        </p>
                      </div>
                      <OverlayTrigger
                        placement="left"
                        overlay={<Tooltip id="refresh-audits">Refresh audits</Tooltip>}
                      >
                        <span>
                          <Button
                            variant="light"
                            size="sm"
                            className="d-flex align-items-center"
                            onClick={handleAuditRefresh}
                            disabled={auditsLoading}
                          >
                            <RefreshCcw size={14} className="me-1" />
                            Refresh
                          </Button>
                        </span>
                      </OverlayTrigger>
                    </div>

                    <div className="mb-4">
                      <select
                        className="form-select"
                        value={selectedAudit?.id || ''}
                        onChange={handleAuditChange}
                        disabled={auditsLoading || audits.length === 0 || isSubmitting}
                      >
                        <option value="" disabled>
                          Select Audit
                        </option>
                        {audits.map(audit => (
                          <option key={audit.id} value={audit.id}>
                            {audit.auditName || 'Unnamed Audit'}
                          </option>
                        ))}
                      </select>
                      <div className="mt-2 small text-muted">
                        {selectedAudit?.auditDescription || 'Awaiting selection'}
                      </div>
                    </div>

                    <div className="d-flex align-items-start justify-content-between mb-3">
                      <div>
                        <h6 className="mb-1">Step 2 · Choose a location</h6>
                        <p className="text-muted small mb-0">
                          Every scan is tagged to the selected location.
                        </p>
                      </div>
                      <OverlayTrigger
                        placement="left"
                        overlay={<Tooltip id="refresh-locations">Refresh locations</Tooltip>}
                      >
                        <span>
                          <Button
                            variant="light"
                            size="sm"
                            className="d-flex align-items-center"
                            onClick={handleLocationRefresh}
                            disabled={locationsLoading}
                          >
                            <RefreshCcw size={14} className="me-1" />
                            Refresh
                          </Button>
                        </span>
                      </OverlayTrigger>
                    </div>
                    <div className="position-relative">
                      <div className="input-group">
                        <span className="input-group-text bg-white">
                          <MapPin className="text-muted" size={16} />
                        </span>
                        <input
                          ref={locationInputRef}
                          type="text"
                          className="form-control"
                          placeholder="Type or scan a location..."
                          value={locationQuery}
                          onChange={handleLocationChange}
                          onFocus={() => setIsLocationListVisible(true)}
                          onBlur={() => {
                            setTimeout(() => setIsLocationListVisible(false), 100);
                          }}
                          onKeyDown={handleLocationKeyDown}
                          disabled={locationsLoading || isSubmitting}
                        />
                      </div>

                      {isLocationListVisible && (
                        <div
                          className="border rounded bg-white shadow-sm position-absolute w-100 mt-1"
                          style={{ maxHeight: '240px', overflowY: 'auto', zIndex: 10 }}
                        >
                          {filteredLocations.length > 0 ? (
                            filteredLocations.map(location => (
                              <button
                                key={location.id || location.locationId}
                                type="button"
                                className="dropdown-item text-start"
                                onMouseDown={event => event.preventDefault()}
                                onClick={() => handleLocationSelect(location)}
                                disabled={isSubmitting}
                              >
                                {location.name || location.locationName || 'Unknown Location'}
                              </button>
                            ))
                          ) : (
                            <div className="dropdown-item text-muted">No locations available</div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="mt-4">
                      <p className="text-uppercase text-muted small mb-2">Current selection</p>
                      <div className="d-flex align-items-start gap-2">
                        <MapPin className="text-primary mt-1" size={18} />
                        <div>
                          <h6 className="mb-0">{locationName}</h6>
                          <p className="text-muted small mb-0">
                            {selectedLocation?.description ||
                              selectedLocation?.address ||
                              'Awaiting selection'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
                      <p className="text-uppercase text-muted small mb-0">Tips</p>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleFetchDetails}
                        disabled={
                          isSubmitting || isFetchingDetails || !selectedAudit || !selectedLocation
                        }
                      >
                        {isFetchingDetails ? 'Fetching...' : 'Fetch Details'}
                      </Button>
                    </div>
                    <ul className="small text-muted ps-3 mb-0">
                      <li>Keep the cursor inside the barcode field before scanning.</li>
                      <li>Most scanners send Enter automatically; manual entry works too.</li>
                      <li>Use the refresh button if you add new locations.</li>
                    </ul>
                  </div>
                </div>
                <div className="col-xl-8">
                  <div className="d-flex flex-column align-items-end">
                    <div
                      className="border rounded p-3 mb-4 w-100  ms-auto"
                    >
                      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
                        <div>
                          <h6 className="mb-1">Step 3 · Scan or type the barcode</h6>
                          <p className="text-muted small mb-0">{scannerHelperText}</p>
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="d-flex align-items-center"
                            onClick={handleClearScans}
                            disabled={isSubmitting}
                          >
                            Clear
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            className="d-flex align-items-center"
                            onClick={handleSubmitScans}
                            disabled={isSubmitting}
                          >
                            {isSubmitting && (
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              />
                            )}
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                          </Button>
                        </div>
                      </div>

                      <div className="input-group">
                        <span className="input-group-text bg-white">
                          <Target size={16} className="text-muted" />
                        </span>
                        <input
                          ref={scanInputRef}
                          type="text"
                          className="form-control"
                          id="barcode-input"
                          placeholder="Scan barcode here..."
                          value={scanData}
                          onChange={handleScanChange}
                          onKeyDown={handleKeyDown}
                          autoFocus
                          disabled={!selectedLocation || isSubmitting}
                        />
                      </div>
                    </div>

                    <div className="d-flex justify-content-end w-100">
                      <div className="border rounded w-100 ms-auto">
                        <div className="table-responsive mb-0">
                          <Table
                            columns={columns}
                            dataSource={pagedScannedItems}
                            pagination={{
                              current: currentPage,
                              pageSize: pageSize,
                              total: scannedItems.length,
                              showSizeChanger: true,
                              pageSizeOptions: ['10', '20', '50', '100'],
                            }}
                            size="middle"
                            rowKey="key"
                            onChange={handleTableChange}
                            locale={{
                              emptyText: 'No items scanned yet',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InventoryScanningView;
